import React, { ChangeEvent, ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import { actionCreators } from '../store/Users';
import { ApplicationState } from '../store';
import { GlobalSettableRoles, OrganizationSettableRoles, useIsUserInRoles, UserFriendlyRoleNames } from '../hooks/useIsUserInRoles';
import { useActiveUser } from '../hooks/useActiveUser';
import { Box, Button, Grid, Typography } from '@material-ui/core';
import { actionCreators as snackbar } from '../store/AlertSnackbarStore'
import { OrganizationResponseDTO, OrganizationsClient, RoleValues, UsersClient, UserWithOrganizationResponseDTO } from '../generated/ApiClient'
import ValuePickerControl from '../common/ValuePickerControl'
import CenteredProgress from '../common/CenteredLoader'
import { useHistory } from 'react-router';

// needed to indicate 'No org' but the value picker control doesn't like undefined
const defaultOrgGuid = '00000000-0000-0000-0000-000000000000'

export default function UserDetailsPage(props: { match: { params: { userId: string }; }; }) {
    const dispatch = useDispatch()
    const userId = props.match.params.userId;
    const history = useHistory()
    const activeUser = useActiveUser()
    const isGlobalAdmin = useIsUserInRoles(['GlobalAdmin'])
    const isOrganizationAdmin = useIsUserInRoles(['OrganizationAdmin'])
    const [selectedUserRole, setSelectedUserRole] = React.useState<RoleValues | undefined>(undefined);
    const [selectedOrganizationId, setSelectedOrganizationId] = React.useState<string | undefined>(undefined);
    const [organizations, setOrganizations] = React.useState<OrganizationResponseDTO[]>([]);

    const isLoading = useSelector<ApplicationState, boolean | undefined>(state => state.users?.isLoading)
    const user = useSelector<ApplicationState, UserWithOrganizationResponseDTO | undefined>(state => {
        return state.users?.byId[userId]
    })

    if (user && selectedUserRole === undefined)
        setSelectedUserRole(user.role.Case)

    if (user && organizations.length > 0 && selectedOrganizationId === undefined)
        setSelectedOrganizationId(user.organization?.externalId ?? defaultOrgGuid)

    React.useEffect(() => {
        dispatch(actionCreators.requestUser(userId));
        const fetchData = async () => {
            const organizationsLocal = await new OrganizationsClient().getMany();
            setOrganizations(organizationsLocal)
        }
        fetchData();
    }, [userId, setOrganizations]);

    const onSaveClick = (user: UserWithOrganizationResponseDTO) => {
        dispatch(actionCreators.updateUser(user, selectedOrganizationId === defaultOrgGuid ? undefined : selectedOrganizationId, selectedUserRole))
    }

    const onRemoveFromOrganizationClick = async (member: UserWithOrganizationResponseDTO) => {
        if (member.organization) {
            try {
                await new OrganizationsClient().deleteUser(member.organization.externalId, userId)
                if (isGlobalAdmin) {
                    const updatedUser = await new UsersClient().get6(member.azureId)
                    dispatch({ type: 'users/ReceiveUsers', users: [updatedUser] })
                }
                else {
                    history.goBack()
                }
                dispatch(snackbar.addMessage({ title: "Removed member from organization", message: 'Successfully removed member from organization', alertType: 'success' }))
            }
            catch (error) {
                dispatch(snackbar.addError(error, undefined, 'Failed to remove member'))
            }
        }
    }

    const onRemoveClick = (user: UserWithOrganizationResponseDTO) => {
        dispatch(actionCreators.removeUser(user.azureId));
    }

    const handleRoleChange = (event: ChangeEvent<{ name?: string | undefined; value: unknown; }>, child: ReactNode) => {
        const target = event.target as HTMLInputElement;
        setSelectedUserRole(target.value as RoleValues)
    };

    const handleOrganizationChange = (event: ChangeEvent<{ name?: string | undefined; value: unknown; }>, child: ReactNode) => {
        const target = event.target as HTMLInputElement;
        setSelectedOrganizationId(target.value)
    };

    const userRoles = (isGlobalAdmin ? GlobalSettableRoles : OrganizationSettableRoles.concat(['Default'])).map(k => ({ text: UserFriendlyRoleNames[k], value: k }))

    const userOrganizations = [{ text: 'Not Assigned', value: defaultOrgGuid }].concat(organizations.map(x => {
        return { text: x.name ?? '', value: x.externalId }
    }))

    const isActiveUser = activeUser.azureId === userId
    const enableOrganizationsPicker = isGlobalAdmin
    const enableRolesPicker = (isGlobalAdmin || isOrganizationAdmin) && !isActiveUser
    const enableSaveButton = (isGlobalAdmin || isOrganizationAdmin)
    const enableRemoveButton = isGlobalAdmin && !isActiveUser
    const enableRemoveFromOrgButton = (isGlobalAdmin || isOrganizationAdmin)

    if (isLoading)
        return <CenteredProgress />
    if (user === undefined)
        return <Typography>Member {userId} not found</Typography>
    else
        return (
            <>
                <Typography variant='h4' id="tabelLabel">{user.displayName}</Typography>
                <Typography paragraph><b>Given Name:</b> {user.givenName}</Typography>
                <Typography paragraph><b>Surname:</b> {user.surname}</Typography>
                <Typography paragraph><b>Email:</b> {user.userName}</Typography>
                <Typography paragraph><b>Current Organization:</b> {(user.organization ? organizations.find(s => s.externalId === user.organization?.externalId)?.name : null) ?? "None Assigned"}</Typography>
                {organizations.length > 0 && selectedOrganizationId &&
                    <ValuePickerControl
                        value={selectedOrganizationId}
                        items={userOrganizations}
                        disabled={!enableOrganizationsPicker}
                        handleChange={handleOrganizationChange} />}
                <Box mt={2}>
                    <Typography paragraph><b>Current Role:</b> {UserFriendlyRoleNames[user.role.Case]}</Typography>
                </Box>
                {selectedUserRole &&
                    <ValuePickerControl
                        value={selectedUserRole}
                        items={userRoles}
                        disabled={!enableRolesPicker}
                        handleChange={handleRoleChange} />}
                <Box m={1}>
                    <Grid container alignItems='baseline' spacing={1} justify='flex-start'>
                        <Grid item>
                            <Button variant='contained' color='primary' onClick={() => onSaveClick(user)} disabled={!enableSaveButton}>Save</Button>
                        </Grid>
                        <Grid item>
                            {user.organization && <Button variant='contained' color='secondary' onClick={() => onRemoveFromOrganizationClick(user)} disabled={!enableRemoveFromOrgButton}>Remove From Organization</Button>}
                        </Grid>
                        <Grid item>
                            <Button variant='contained' color='secondary' onClick={() => onRemoveClick(user)} disabled={!enableRemoveButton}>Remove</Button>
                        </Grid>
                    </Grid>
                </Box>
            </>
        )
}
